import { useState } from "react";
import { Megaphone, Pencil, Trash2, Settings, KeyRound, Ticket } from "lucide-react";
import { Button, Icon } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useAnnouncements, useFanClubOverview, useRetractAnnouncement } from "../../app/hooks/useAnnouncements.js";
import { errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { statusMeta, typeMeta, shortDate } from "./announcementMeta.js";
import {
  AnnouncementLoading,
  AnnouncementsEmpty,
  AnnouncementQueryError,
} from "./AnnouncementStates.js";
import { HubMetrics } from "./HubMetrics.js";
import { SeenCtrBar } from "./SeenCtrBar.js";
import { MerchShelfCard } from "./MerchShelfCard.js";
import styles from "./AntennaHubScreen.module.scss";

export interface AntennaHubScreenProps {
  onNewAnnouncement: () => void;
  onEditAnnouncement: (id: string) => void;
  /** Open the fan-club settings modal (shot-42). Supplied by the AntennaRoute wrapper via navigate. */
  onOpenSettings?: () => void;
  /** Open the backstage manager modal.*/
  onOpenBackstage?: () => void;
  /** Open the comps manager modal.*/
  onOpenComps?: () => void;
  /**
   * Force the read posture regardless of `antenna:manage` — the ROSTER mount (nav-parity CR).
   * A management org seat genuinely holds `antenna:manage`, so without this it would be offered a
   * composer whose routed-modal children exist only under the artist shell: a button that opens
   * nothing, which is the dead-button failure the roster specs call out by name. Authoring stays on
   * the artist surface until the roster grows its own composer routes.
   */
  readOnly?: boolean;
}

/**
 * The Antenna fan-club hub (`/antenna`; shot-37). A4 grows the list-only surface into the KPI hub:
 * the 5-card metric strip + Membership tier breakdown (<see cref="HubMetrics"/>) sourced from the Antenna
 * band-metrics API (own-club-only) and the per-announcement seen/CTR bars — all born-blank when
 * Antenna is unavailable and NEVER fabricating a number (F-06). The backstage/comps managers + the
 * fan-club settings modal (shot-42) are the remaining A4 surfaces.
 *
 * The composer entry + row actions are gated on `antenna:manage` (capabilities-as-data) — a Label
 * seat holds `antenna:read` and sees the hub read-only, matching the server 403 (defense in depth).
 */
export function AntennaHubScreen({
  onNewAnnouncement,
  onEditAnnouncement,
  onOpenSettings,
  onOpenBackstage,
  onOpenComps,
  readOnly = false,
}: AntennaHubScreenProps) {
  // The token is still the authority; `readOnly` can only ever SUBTRACT from it, never grant.
  const canManage = useCapability("antenna:manage") && !readOnly;
  const list = useAnnouncements();
  const overview = useFanClubOverview();
  const retract = useRetractAnnouncement();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const rows = list.data?.items ?? [];
  const live = rows.filter((r) => r.status === "pub").length;
  const drafts = rows.filter((r) => r.status === "draft").length;

  // (shot-37): the monthly publish-quota clause. A pure core-DB fact on the overview (never degrades
  // when Antenna is down); F-06 — render nothing until the overview is ready, never a fabricated number.
  const o = overview.data;
  const quotaReady = !overview.isLoading && !overview.isError && o != null;
  // The generated client types the overview counts as optional (Swashbuckle emits no `required`), so coalesce —
  // the backend always computes them (a pure core fact), matching the existing HubMetrics `?? 0` pattern.
  const publishQuota = o?.publishQuota ?? 0;
  const publishesLeft = quotaReady ? Math.max(0, publishQuota - (o.publishesUsedThisMonth ?? 0)) : null;

  function onRetract(id: string) {
    retract.mutate(id, {
      onSuccess: () => setConfirmingId(null),
      onError: (err) => pushToast(errorMessage(err, "Couldn't retract the announcement.")),
    });
  }

  return (
    <section className={styles.screen} aria-labelledby="antenna-hub-title">
      <header className={styles.header}>
        <div>
          <h1 id="antenna-hub-title" className={styles.title}>
            Your fan club
          </h1>
        </div>
        {canManage && (
          <Button variant="accent" onClick={onNewAnnouncement}>
            <Icon icon={Megaphone} size={16} /> New announcement
          </Button>
        )}
      </header>

      <HubMetrics />

      {/* Management entry points (canManage-gated, capabilities-as-data) — the shot-42 fan-club
          settings modal + the backstage/comps managers, each opened as a routed-modal child.*/}
      {canManage && (onOpenSettings || onOpenBackstage || onOpenComps) && (
        <div className={styles.manageBar}>
          {onOpenSettings && (
            <Button variant="ghost" size="sm" onClick={onOpenSettings}>
              <Icon icon={Settings} size={16} /> Fan club settings
            </Button>
          )}
          {onOpenBackstage && (
            <Button variant="ghost" size="sm" onClick={onOpenBackstage}>
              <Icon icon={KeyRound} size={16} /> Backstage
            </Button>
          )}
          {onOpenComps && (
            <Button variant="ghost" size="sm" onClick={onOpenComps}>
              <Icon icon={Ticket} size={16} /> Comps
            </Button>
          )}
        </div>
      )}

      <div className={styles.listHead}>
        <p className={styles.sectionLabel}>Announcements</p>
        {rows.length > 0 && (
          <p className={styles.tally}>
            {live} live · {drafts} draft
            {publishesLeft !== null && (
              <> · {publishesLeft} of {publishQuota} publishes left this month (shows, tours &amp; on-sales don&rsquo;t count)</>
            )}
          </p>
        )}
      </div>

      {list.isLoading ? (
        <AnnouncementLoading />
      ) : list.isError ? (
        <AnnouncementQueryError error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <AnnouncementsEmpty onNew={onNewAnnouncement} canManage={canManage} />
      ) : (
        <ul className={styles.rows}>
          {rows.map((row) => {
            const id = row.announcementId ?? "";
            const t = typeMeta(row.type);
            const s = statusMeta(row.status);
            const isDraft = row.status === "draft";
            const confirming = confirmingId === id;
            return (
              <li key={id} className={styles.row}>
                <div className={styles.rowBadges}>
                  <span className={styles.typeBadge}>
                    <Icon icon={t.icon} size={16} />
                    {t.label}
                  </span>
                  <span className={styles.statusBadge} data-tone={s.tone}>
                    {s.label}
                  </span>
                </div>
                <div className={styles.rowMain}>
                  <span className={styles.rowTitle}>{row.title || "Untitled"}</span>
                  <span className={styles.rowMeta}>
                    {row.audienceTierId ? "Members only" : "Everyone"}
                    {row.publishedOn || row.createdOn ? (
                      <>
                        {" · "}
                        {shortDate(row.publishedOn ?? row.createdOn)}
                      </>
                    ) : null}
                  </span>
                </div>
                {/* Seen/CTR bar from the band-metrics API (A4); a null count (draft / Antenna down) degrades to —. */}
                <SeenCtrBar seenCount={row.seenCount} clickCount={row.clickCount} />
                {canManage && (
                  <div className={styles.rowActions}>
                    {confirming ? (
                      <>
                        <Button
                          variant="accent"
                          size="sm"
                          loading={retract.isPending}
                          onClick={() => onRetract(id)}
                        >
                          Retract
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={retract.isPending}
                          onClick={() => setConfirmingId(null)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        {isDraft && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label={`Edit ${row.title || "draft"}`}
                            onClick={() => onEditAnnouncement(id)}
                          >
                            <Icon icon={Pencil} size={16} />
                          </button>
                        )}
                        {row.status !== "retracted" && (
                          <button
                            type="button"
                            className={styles.iconBtn}
                            aria-label={`Retract ${row.title || "announcement"}`}
                            onClick={() => setConfirmingId(id)}
                          >
                            <Icon icon={Trash2} size={16} />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* The merch shelf — a pure born-blank placeholder (no endpoint; core Phase 6). */}
      <div className={styles.merchSlot}>
        <MerchShelfCard />
      </div>
    </section>
  );
}
